import { EventInput } from '@fullcalendar/core'
import { IconifyIcons } from '@thyme/nashville/src/types/icons'
import get from 'lodash-es/get'
import { DateTime } from 'luxon'
import { nashColors } from 'tailwind.config.ts'
// nosemgrep: no-v2-imports
import { SCHEDULED_CALL_TIME_SET_DURATION } from '@/legacy/components/patient/communicationV2/lib/plannedCalls'
// nosemgrep: no-legacy-api-store
import apiStore from '@/legacy/store/modules/apiBuilder'
import { Disposition } from '@/legacy/types/communications/callDispositions'
import {
  Communication,
  CommunicationType,
  CommunicationParts,
} from '@/legacy/types/communications/communications'
import { GenericListResponse } from '@/libs/api/types'
import { formatNameFromEntity } from '@/libs/format/format'

export const useScheduleApi = apiStore<EventInput, EventInput[], Communication>(
  'scheduleApi',
  '/api/communications',
  {
    transformData: (d: GenericListResponse<Communication>) => ({
      ...d,
      data: d.data.map((communication: Communication) => {
        // If call was completed, use its actual time of completion in My Schedule
        // If call is still scheduled, use the scheduled datetime
        let startTime =
          communication.completedDatetime ??
          communication.plannedCall?.dueDatetime
        // convert to UTC
        if (startTime) {
          startTime += 'Z'
        }
        const duration =
          communication.plannedCall?.duration ??
          SCHEDULED_CALL_TIME_SET_DURATION

        // if start time specified but duration is not 30 minutes, the call is scheduled
        // but only for a day and not for a specific time, so do not set end time
        const endTime =
          startTime && duration === SCHEDULED_CALL_TIME_SET_DURATION
            ? DateTime.fromISO(startTime).plus({ minutes: duration }).toISO()
            : null
        const patient = get(communication, 'patients[0]')
        const callWith = patient ? formatNameFromEntity(patient) : 'UNKNOWN'
        let eventColor
        let textColor
        let icon
        if (
          communication.callDisposition?.disposition === Disposition.ANSWERED
        ) {
          eventColor = nashColors.fern100
          textColor = nashColors.fern700
          icon = IconifyIcons.check
        } else if (communication.callDisposition) {
          eventColor = nashColors.neutral200
          textColor = nashColors.neutral700
          icon = IconifyIcons.close
        }

        // put in "no call time" bucket (all day) if we do not want to display time
        let allDay = false
        // explicitly check for showTime === false, we do want to display in other scenarios
        if (
          !communication.callDisposition &&
          communication.plannedCall?.showTime === false
        ) {
          allDay = true
        }

        return {
          title: `Call with ${callWith}`,
          allDay,
          start: startTime,
          end: endTime,
          id: communication.communicationId,
          ...(eventColor
            ? { backgroundColor: eventColor, borderColor: eventColor }
            : {}),
          ...(textColor ? { textColor } : {}),
          extendedProps: {
            ...(icon ? { icon } : {}),
            patientId: patient?.entityId,
          },
        } as EventInput
      }),
    }),
    params: {
      parts: [
        CommunicationParts.planned,
        CommunicationParts.disposition,
        CommunicationParts.patients,
      ],
      filter_types: [CommunicationType.Call],
      filter_unassigned: 'EXCLUDE',
    },
  }
)
